import { Block, Check } from "@mui/icons-material";
import { Box, Divider, IconButton, Paper, Stack } from "@mui/material";
import dayjs from "dayjs";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FiExternalLink } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { AppointmentType } from "../../../../components/modals/bookingModalComponents/context/BookingModalContext";
import { STATUSES } from "../../../../enums/AppointmentStatus";
import { PENDING_APPOINTEMENT_DETAILS } from "../../../../enums/UrlPaths";
import useConfirm from "../../../../hooks/useConfirm";
import useServiceTranslation from "../../../../hooks/useServiceTranslation";
import { getDateObject } from "../../../../utils/DateTimeRepository";
import { trimText } from "../../../../utils/Functions";
import AppointmentShortInfo from "../../../appointments/components/display/AppointmentShortInfo";
import usePendingStatus from "../hooks/usePendingStatus";
import { parsePendingAppointment } from "../utils/Functions";
import Calendar from "./Calendar";
import PendingConfirmationTemplate from "./PendingConfirmationTemplate";

export default function PendingAppointmentBody({
  appointment,
  slimVersion = false,
  forceRefetch,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const put = usePendingStatus(appointment?.id);

  const service = useServiceTranslation(appointment[AppointmentType]);

  const { ConfirmationDialog, confirm, getMessage } = useConfirm(
    t("AreYouSure"),
    t("Accept"),
    t("Cancel"),
    "warning"
  );

  const methods = useForm({
    defaultValues: {
      statusMessage: `${t("Dear")} ${appointment?.ownerName}, ${t(
        "RejectDefaultMessage"
      )}`,
    },
  });

  const dateParse = () =>
    dayjs(getDateObject(appointment?.startDate)).format("DD/MM/YYYY, HH:mm");

  const handleStatusAppointment = async (acceptance_status) => {
    getMessage(
      <FormProvider {...methods}>
        <PendingConfirmationTemplate status={acceptance_status} />
      </FormProvider>
    );
    const ans = await confirm();
    const submitData = parsePendingAppointment(
      methods.getValues("statusMessage")
    );

    if (ans) {
      if (acceptance_status == STATUSES.ACTIVE) {
        put.acceptFn(submitData);
      } else if (acceptance_status == STATUSES.INACTIVE) {
        put.rejectFn(methods.getValues("statusMessage"));
      }
      if (forceRefetch) {
        forceRefetch();
      }
    }
  };

  if (slimVersion) {
    return (
      <>
        <Paper
          sx={{
            height: "100%",
            p: 2,
          }}
          data-testid={`appointment.pending-card.${appointment?.id}`}
        >
          <Stack direction={"row"} justifyContent="space-between" spacing={1}>
            <AppointmentShortInfo
              petName={trimText(appointment?.petName, 30)}
              date={dateParse()}
              type={service}
              user={appointment.ownerEmail}
            />
            <Stack direction={"row"}>
              <Stack>
                <Calendar
                  id={appointment?.id}
                  startDate={appointment?.startDate}
                />
                <IconButton
                  title={t("Accept")}
                  color="info"
                  onClick={() => handleStatusAppointment(STATUSES.ACTIVE)}
                  data-testid={`appointment.pending-card.${appointment?.id}.accept-button`}
                >
                  <Check />
                </IconButton>
              </Stack>
              <Stack>
                <IconButton
                  title={t("ShowDetails")}
                  onClick={() => {
                    navigate(PENDING_APPOINTEMENT_DETAILS + appointment?.id);
                  }}
                  data-testid={`appointment.pending-card.${appointment?.id}.details-button`}
                >
                  <FiExternalLink />
                </IconButton>
                <IconButton
                  title={t("Reject")}
                  color="error"
                  onClick={() => handleStatusAppointment(STATUSES.INACTIVE)}
                  data-testid={`appointment.pending-card.${appointment?.id}.reject-button`}
                >
                  <Block />
                </IconButton>
              </Stack>
            </Stack>
          </Stack>
        </Paper>
        <ConfirmationDialog />
      </>
    );
  }

  return (
    <>
      <Paper
        sx={{
          minHeight: 200,
          height: "100%",
          p: 2,
        }}
        data-testid={`appointment.pending-card.${appointment?.id}`}
      >
        <Stack height="100%" justifyContent="space-between" spacing={1}>
          <AppointmentShortInfo
            petName={trimText(appointment?.petName, 30)}
            date={dateParse()}
            type={service}
            user={appointment.ownerEmail}
          />
          <Divider />
          <Stack direction={"row"} alignItems={"center"} spacing={2}>
            <IconButton
              title={t("Accept")}
              color="info"
              onClick={() => handleStatusAppointment(STATUSES.ACTIVE)}
              data-testid={`appointment.pending-card.${appointment?.id}.accept-button`}
            >
              <Check />
            </IconButton>
            <IconButton
              title={t("Reject")}
              color="error"
              onClick={() => handleStatusAppointment(STATUSES.INACTIVE)}
              data-testid={`appointment.pending-card.${appointment?.id}.reject-button`}
            >
              <Block />
            </IconButton>
            <Calendar id={appointment?.id} startDate={appointment?.startDate} />
            <Box width={"50%"} justifyContent={"end"} display={"flex"}>
              <IconButton
                title={t("ShowDetails")}
                onClick={() => {
                  navigate(PENDING_APPOINTEMENT_DETAILS + appointment?.id);
                }}
                data-testid={`appointment.pending-card.${appointment?.id}.details-button`}
              >
                <FiExternalLink />
              </IconButton>
            </Box>
          </Stack>
        </Stack>
      </Paper>
      <ConfirmationDialog />
    </>
  );
}
